import { useEffect } from 'react';
import { connectToSocket, socket } from '../api/socket';
import { queryClient } from '../queryClient';
import Task from '../types/Task';
import Workspace from '../types/Workspace';
import { useGetWorkspace } from '../serverState/workspace';
import { Epic } from '../types/Epic';
import { WorkspaceView } from '../types/WorkspaceView';
import { TaskType } from '../types/TaskType';
import { notification } from 'antd';
import { CheckCircleFilled, WarningFilled } from '@ant-design/icons';
import { reconcileStageChange } from '../serverState/helpers/taskStageCache';
import { queryKeys } from '../serverState/queryKeys';
import {
  mapListCache,
  removeListItems,
  removeListItemsEverywhere,
  updateWorkspaceViews,
  upsertToEnd,
} from '../serverState/helpers/listCache';
import { RealtimeMeta, decodeSocketPayload } from './socketPayload';
import {
  isAnyTaskMutating,
  whenAllMutationsSettle,
  whenTaskMutationsSettle,
} from '../serverState/helpers/inFlightTasks';
import { PayloadOf } from '../types/realtimePayloads';
import { writeTaskDetailCache, removeTaskDetailCache, invalidateTaskDetailCache } from '../serverState/tasks';
// Re-exported so existing importers of `SocketEvent` from this module keep
// compiling. The single source of truth is the synced contract.
import { SocketEvent } from '../types/realtime-contract';
export { SocketEvent };

export const useSpaceSocket = (spaceId?: string) => {
  const workspaceQuery = useGetWorkspace();

  useEffect(() => {
    // Don't set up socket handlers if we don't have a valid spaceId
    if (!spaceId) {
      return;
    }

    let aborted = false;

    // Cleanup handles for reconciliations still waiting on in-flight mutations.
    const pendingReconciles = new Set<() => void>();

    const runWhenSettled = (subscribe: (cb: () => void) => () => void, action: () => void) => {
      const entry = { cancel: () => undefined as void };
      let fired = false;
      entry.cancel = subscribe(() => {
        fired = true;
        pendingReconciles.delete(entry.cancel);
        if (aborted) return;
        action();
      });
      if (!fired) {
        pendingReconciles.add(entry.cancel);
      }
    };

    /**
     * Once the server stops excluding the initiator, this tab receives echoes of its own
     * writes. An echo carrying pre-commit state must not overwrite a newer local edit — e.g.
     * TaskView's 1s-debounced description save — so an update for a task with a *keyed* write
     * in flight is skipped. Writes that don't run through a keyed mutation are invisible here,
     * which is deliberate: their echo applies and repairs whatever cache they forgot to patch.
     *
     * What happens next depends on who caused it. Our own echo needs nothing: the mutation that
     * is still running owns this task's cache entry. A *peer's* change would otherwise be lost,
     * so it schedules a refetch for when our write settles. The distinction matters — the server
     * emits right after commit, so our own echo almost always lands while the mutation is still
     * pending, and reconciling those would refetch the whole task list on every single edit.
     *
     * Only update events need this. Deletes are terminal and idempotent: re-applying one is a
     * no-op, and deferring them would cost a full list refetch on every batch delete.
     */
    const deferUpdateWhileMutating = (taskIds: number[], meta: RealtimeMeta | null): boolean => {
      if (!isAnyTaskMutating(queryClient, taskIds)) {
        return false;
      }
      // Envelope-less events carry no socket identity, so a self-echo is indistinguishable
      // from a peer's change and is reconciled as a peer's. Since #3056 the server excludes
      // the initiating socket whenever it knows it, and only a client too old to send
      // x-socket-id receives its own echo here, so treating these as a peer's costs a
      // refetch for legacy clients only, and never drops a real peer change.
      const isSelfEcho = !!meta?.actorSocketId && meta.actorSocketId === socket.id;
      if (!isSelfEcho) {
        runWhenSettled(
          (cb) => whenTaskMutationsSettle(queryClient, taskIds, cb),
          () => {
            const spaceKey = Number(spaceId);
            // Epics are their own key family. `['tasks', id]` prefix-matches the archive
            // sibling but never `['epics', id]`, so a deferred edit to an epic would be
            // dropped — and the handler we skipped would have patched it precisely.
            void queryClient.invalidateQueries({ queryKey: queryKeys.tasks(spaceKey), refetchType: 'active' });
            void queryClient.invalidateQueries({ queryKey: queryKeys.epics(spaceKey), refetchType: 'active' });
          }
        );
      }
      return true;
    };

    // --- Named handler functions ---

    const handleTaskUpdated = (raw: unknown) => {
      const { data: updatedTask, meta } = decodeSocketPayload<PayloadOf<SocketEvent.taskUpdated>>(raw);
      if (!updatedTask) {
        return;
      }
      if (deferUpdateWhileMutating([updatedTask.id], meta)) {
        return;
      }

      const prevDefault = (queryClient.getQueryData(queryKeys.tasks(Number(spaceId))) as Task[] | undefined) || [];
      const prevArchive =
        (queryClient.getQueryData(queryKeys.archivedTasks(Number(spaceId))) as Task[] | undefined) || [];
      const prevStage =
        prevDefault.find((t) => t.id === updatedTask.id)?.stage ??
        prevArchive.find((t) => t.id === updatedTask.id)?.stage;

      // Deliberately deferred until AFTER the list patches below. reconcileStageChange
      // ends an archive-boundary crossing with invalidateQueries on the destination list,
      // and the patches below call setQueryData on those same keys — which marks the query
      // successfully updated and CLEARS the invalidation. Because mapListCache only maps
      // rows already present and never inserts, the destination cache would be left looking
      // fresh while missing the task, and staleTime (24h) means it is never refetched: you
      // archive a task, open Archive, and it is not there. Running it last keeps the
      // invalidation as the final word. (#3010-followup)
      const applyStageChange = () => {
        if (updatedTask.stage && prevStage !== updatedTask.stage) {
          reconcileStageChange(Number(spaceId), updatedTask.id, prevStage, {
            stage: updatedTask.stage,
            archivedAt: updatedTask.archivedAt,
          });
        }
      };

      if (updatedTask.type === TaskType.epic) {
        mapListCache<Epic>(queryClient, queryKeys.epics(Number(spaceId)), (epic) =>
          epic.id === updatedTask.id ? { ...updatedTask, TaskLabels: updatedTask.TaskLabels ?? epic.TaskLabels } : epic
        );
        applyStageChange();
        void queryClient.invalidateQueries({ queryKey: queryKeys.taskLinks(Number(updatedTask.id)) });
        void queryClient.invalidateQueries({ queryKey: queryKeys.taskAttachments(Number(updatedTask.id)) });
        // Task View reads the detail record, not the list row, so a peer's edit has to
        // land here too or the open task view stays stale until a reload (#3010).
        writeTaskDetailCache(updatedTask);
        return;
      }

      // The taskUpdated payload doesn't include dependency edges (the update query
      // omits them), so preserve the existing edges — same as TaskLabels — to keep
      // the Blocking / Blocked By columns from blanking on unrelated edits.
      mapListCache<Task>(queryClient, queryKeys.tasks(Number(spaceId)), (t) =>
        t.id === updatedTask.id
          ? {
              ...updatedTask,
              key: updatedTask.id,
              TaskLabels: updatedTask.TaskLabels ?? t.TaskLabels,
              BlockingEdges: updatedTask.BlockingEdges ?? t.BlockingEdges,
              BlockedByEdges: updatedTask.BlockedByEdges ?? t.BlockedByEdges,
            }
          : t
      );
      // Archived tasks live in a separate cache the default patch above never touches,
      // so a same-stage edit to an archived task wouldn't reach the Archive view. Mirror
      // the patch; mapListCache is a no-op when the task isn't in this cache.
      mapListCache<Task>(queryClient, queryKeys.archivedTasks(Number(spaceId)), (t) =>
        t.id === updatedTask.id
          ? {
              ...updatedTask,
              key: updatedTask.id,
              TaskLabels: updatedTask.TaskLabels ?? t.TaskLabels,
              BlockingEdges: updatedTask.BlockingEdges ?? t.BlockingEdges,
              BlockedByEdges: updatedTask.BlockedByEdges ?? t.BlockedByEdges,
            }
          : t
      );
      // Task View reads the detail record, not the list row, so a peer's edit has to
      // land here too or the open task view stays stale until a reload (#3010).
      // Safe before applyStageChange: this writes the ['taskDetail'] key family, never the
      // list keys, so it cannot clear the stage invalidation below.
      writeTaskDetailCache(updatedTask);
      // Last, so its invalidation is not cleared by the setQueryData patches above.
      applyStageChange();
      void queryClient.invalidateQueries({ queryKey: queryKeys.taskLinks(Number(updatedTask.id)) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.taskAttachments(Number(updatedTask.id)) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.taskSubtasks(Number(updatedTask.parentTaskId ? updatedTask.parentTaskId : updatedTask.id)),
      });
      if (updatedTask.epicId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.taskSubtasks(updatedTask.epicId) });
      }
    };

    const handleTaskDependenciesChanged = (raw: unknown) => {
      const { data: parsed, meta } = decodeSocketPayload<{ taskId?: number; relatedTaskId?: number }>(raw);
      if (!parsed?.taskId) {
        return;
      }
      // Our own echo needs nothing: useAddTaskDependency / useRemoveTaskDependency already
      // brought every one of these keys up to date in onSuccess. Reconciling it anyway is the
      // "refetch the whole task list on every single edit" cost §4.2 warns about, and it undid
      // their targeted list patch — one Blocking / Blocked By toggle refetched the entire space
      // twice. Envelope-less events predate the contract and can only be a peer's, so they
      // still reconcile. (#2803)
      if (meta?.actorSocketId && meta.actorSocketId === socket.id) {
        return;
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.taskDependencies(Number(parsed.taskId)) });
      if (parsed.relatedTaskId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.taskDependencies(Number(parsed.relatedTaskId)) });
      }
      // Refresh the table list so the Blocking / Blocked By columns reflect the change.
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks(Number(spaceId)) });
      // And the detail record, which Task View renders and saveTask spreads back onto the
      // list row — a stale edges array there survives the list refetch above and is written
      // over the fresh row on the next save. (#3010)
      invalidateTaskDetailCache([Number(parsed.taskId), Number(parsed.relatedTaskId)].filter(Boolean));
    };

    const handleTaskAdded = (raw: unknown) => {
      // Self-echo policy (audit §4.2): the socket that initiated the create
      // already inserted via its API response — skip by exact socket identity.
      // Envelope-less events come from pre-contract servers and carry no socket
      // identity, so there is nothing to skip by. The old fallback guessed with
      // `createdBy === user.id`, which dropped the event in every tab the
      // creator had open instead of only the initiating one, so a task created
      // in one tab never reached another (#3056). Applying it is safe: the
      // insert below is an upsert keyed by id and list position comes from the
      // workspace TaskOrder, not cache array order. Update/delete handlers
      // intentionally do NOT skip either: they're idempotent and the echo is a
      // reconciliation checkpoint.
      const { data: addedTask, meta } = decodeSocketPayload<Task>(raw);
      if (!addedTask) {
        return;
      }
      if (meta?.actorSocketId && meta.actorSocketId === socket.id) {
        return;
      }
      if (addedTask.stage === 'archive') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.archivedTasks(Number(spaceId)) });
      } else {
        upsertToEnd<Task>(queryClient, queryKeys.tasks(Number(spaceId)), { ...addedTask, key: addedTask.id });
      }
      if (addedTask.parentTaskId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.taskSubtasks(addedTask.parentTaskId) });
      }
      if (addedTask.epicId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.taskSubtasks(addedTask.epicId) });
      }
      if (addedTask.type === TaskType.epic) {
        upsertToEnd<Epic>(queryClient, queryKeys.epics(Number(spaceId)), addedTask as unknown as Epic);
      }
    };

    const handleTaskDeleted = (raw: unknown) => {
      const { data } = decodeSocketPayload<{ id?: number | string; ids?: Array<number | string>; type?: string }>(raw);
      if (!data) {
        return;
      }
      const ids = (data.ids ?? (data.id != null ? [data.id] : [])).map(Number).filter((n) => !Number.isNaN(n));
      if (ids.length === 0) {
        return;
      }

      // Epic detection: trust the payload's type, and fall back to the epics
      // cache so legacy payloads without a type still clean up correctly.
      const cachedEpics = queryClient.getQueryData<Epic[]>(queryKeys.epics(Number(spaceId))) ?? [];
      const deletedEpicIds =
        data.type === TaskType.epic ? ids : ids.filter((id) => cachedEpics.some((e) => e.id === id));

      removeListItems<Task>(queryClient, queryKeys.tasks(Number(spaceId)), ids);
      removeListItems<Task>(queryClient, queryKeys.archivedTasks(Number(spaceId)), ids);
      // A subitem also hangs in its parent's list, and the payload never names that
      // parent, so every subtask cache is swept. Epic children read the same key, so
      // this covers the epic to item relation too (#3100).
      removeListItemsEverywhere<Task>(queryClient, queryKeys.taskSubtasksRoot(), ids);
      // The detail record is keyed by task NUMBER, so it survives the list evictions above
      // and would be served to the next open of that URL. This does NOT close a Task View
      // that is already showing the deleted task — useTaskDetails keeps the task it holds
      // unless the taskId changes (useTaskDetails.tsx:82-88), same as before #3010.
      removeTaskDetailCache(ids);

      if (deletedEpicIds.length > 0) {
        mapListCache<Task>(queryClient, queryKeys.tasks(Number(spaceId)), (task) =>
          task.epicId && deletedEpicIds.includes(task.epicId) ? { ...task, epicId: null } : task
        );
        removeListItems<Epic>(queryClient, queryKeys.epics(Number(spaceId)), deletedEpicIds);
      }
    };

    const handleTaskCommentsChanged = (raw: unknown) => {
      const { data: parsed } = decodeSocketPayload<{ id: number }>(raw);
      if (!parsed?.id) return;
      void queryClient.invalidateQueries({ queryKey: queryKeys.taskComments(parsed.id) });
    };

    const handleTaskActivityChanged = (raw: unknown) => {
      const { data: parsed } = decodeSocketPayload<{ id: number }>(raw);
      if (!parsed?.id) return;
      void queryClient.invalidateQueries({ queryKey: queryKeys.taskActivity(parsed.id) });
    };

    const handleTasksUpdated = (raw: unknown) => {
      const { data: updatedTasks, meta } = decodeSocketPayload<PayloadOf<SocketEvent.tasksUpdated>>(raw);
      if (!updatedTasks) {
        return;
      }
      if (deferUpdateWhileMutating(updatedTasks.tasks, meta)) {
        return;
      }

      const { clearSprintStatus, planningSessionId, sprintId, tasks, stage, archivedAt, ...updatedFields } =
        updatedTasks;

      let applyStageMoves: (() => void) | undefined;

      if (stage) {
        const defaultKey = queryKeys.tasks(Number(spaceId));
        const archiveKey = queryKeys.archivedTasks(Number(spaceId));
        const prevDefault = (queryClient.getQueryData(defaultKey) as Task[] | undefined) || [];
        const prevArchive = (queryClient.getQueryData(archiveKey) as Task[] | undefined) || [];
        const enteringArchive: number[] = [];
        const leavingArchive: number[] = [];
        tasks.forEach((taskId) => {
          const existingStage =
            prevDefault.find((t) => t.id === taskId)?.stage ?? prevArchive.find((t) => t.id === taskId)?.stage;
          if (existingStage === stage) return;
          if (stage === 'archive' && existingStage !== 'archive') enteringArchive.push(taskId);
          else if (existingStage === 'archive' && stage !== 'archive') leavingArchive.push(taskId);
        });
        // Deferred until AFTER the mapListCache below, for the same reason as the
        // single-task handler: the invalidation here has to be the last write to the
        // destination key, or setQueryData clears it and the destination list is left
        // looking fresh while missing the rows that just moved into it. (#3010-followup)
        applyStageMoves = () => {
          if (enteringArchive.length > 0) {
            queryClient.setQueryData<Task[]>(defaultKey, (old) =>
              old ? old.filter((t) => !enteringArchive.includes(t.id)) : old
            );
            void queryClient.invalidateQueries({ queryKey: archiveKey });
          }
          if (leavingArchive.length > 0) {
            queryClient.setQueryData<Task[]>(archiveKey, (old) =>
              old ? old.filter((t) => !leavingArchive.includes(t.id)) : old
            );
            void queryClient.invalidateQueries({ queryKey: defaultKey });
          }
        };
      }

      mapListCache<Task>(queryClient, queryKeys.tasks(Number(spaceId)), (task) => {
        if (!tasks.includes(task.id)) {
          return task;
        }

        if (sprintId || planningSessionId || clearSprintStatus) {
          return {
            ...task,
            planningSessionId: planningSessionId || (clearSprintStatus || sprintId ? null : task.planningSessionId),
            sprintId: sprintId || (clearSprintStatus || planningSessionId ? null : task.sprintId),
          };
        }

        const existingLabels = task.TaskLabels || [];
        const newLabelIds = updatedTasks.labelIds || [];

        if (updatedTasks.action === 'remove') {
          return {
            ...task,
            ...updatedFields,
            TaskLabels: task.TaskLabels?.filter((tl) => !updatedFields.labelIds?.includes(tl.labelId)),
          };
        }

        // Keep existing labels that aren't in the new labels list
        const preservedLabels = existingLabels.filter((tl) => !newLabelIds.includes(tl.labelId));

        // Merge preserved labels with new labels. These rows carry no `id`: the server
        // assigns it, and deriving one (it used to be `task.id + labelId`) produces ids
        // that collide across tasks the moment the same event is applied twice.
        const mergedLabels = [
          ...preservedLabels,
          ...newLabelIds.map((labelId) => ({
            labelId,
            taskId: task.id,
          })),
        ];

        return {
          ...task,
          ...updatedFields,
          TaskLabels: mergedLabels,
        };
      });

      // Bulk peer edits reach the open Task View the same way single ones do (#3010).
      // Invalidate rather than hand-merge: stage/sprintId/planningSessionId/archivedAt and
      // the label preserve/merge logic are computed above, inside this closure, and never
      // leave it — mirroring them here would be a second copy of that merge that can drift
      // from this one. staleTime: 0 + refetchOnMount: 'always' + an open Task View being a
      // live observer means invalidateQueries's default `refetchType: 'active'` refetches
      // it immediately, correct by construction (review finding on #3010).
      // Touches the ['taskDetail'] key family only, so it cannot clear the list
      // invalidation applyStageMoves performs below.
      invalidateTaskDetailCache(tasks);
      // Last, so the destination-list invalidation is not cleared by the patch above.
      applyStageMoves?.();
    };

    const handleTasksDeleted = (raw: unknown) => {
      const { data } = decodeSocketPayload<{ tasks?: Array<number | string>; ids?: Array<number | string> }>(raw);
      const ids = (data?.tasks ?? data?.ids ?? []).map(Number).filter((n) => !Number.isNaN(n));
      if (ids.length === 0) {
        return;
      }

      const currentEpics = (queryClient.getQueryData(queryKeys.epics(Number(spaceId))) as Epic[]) || [];
      const deletedEpicIds = currentEpics.filter((epic) => ids.includes(epic.id)).map((epic) => epic.id);

      removeListItems<Epic>(queryClient, queryKeys.epics(Number(spaceId)), ids);
      removeListItems<Task>(queryClient, queryKeys.tasks(Number(spaceId)), ids);
      // Archived rows live in their own cache; the single-task delete handler already
      // evicts from both, so mirror it here or the Archive view keeps ghosts.
      removeListItems<Task>(queryClient, queryKeys.archivedTasks(Number(spaceId)), ids);
      // Same reasoning as the single-delete handler: a bulk delete can take subitems
      // with it, and the payload names no parent (#3100).
      removeListItemsEverywhere<Task>(queryClient, queryKeys.taskSubtasksRoot(), ids);
      // Same reasoning as the single-delete handler: the number-keyed detail records
      // outlive the list evictions, so drop them rather than serve them on the next open
      // of those URLs. An already-open Task View keeps rendering its copy either way.
      removeTaskDetailCache(ids);
      if (deletedEpicIds.length > 0) {
        mapListCache<Task>(queryClient, queryKeys.tasks(Number(spaceId)), (task) =>
          task.epicId && deletedEpicIds.includes(task.epicId) ? { ...task, epicId: null } : task
        );
      }
    };

    const handleEpicUpdated = (raw: unknown) => {
      const { data: updatedEpic } = decodeSocketPayload<PayloadOf<SocketEvent.epicUpdated>>(raw);
      if (!updatedEpic) {
        return;
      }

      mapListCache<Epic>(queryClient, queryKeys.epics(Number(spaceId)), (epic) =>
        epic.id === updatedEpic.id ? updatedEpic : epic
      );
      // Epics are rows of the Task table and Task View resolves them from the detail
      // record too (getTaskDetailByNumber has no type filter), so an epic renamed from
      // the Backlog header by a peer has to land here or the open view stays stale until
      // a reload. An epic edited THROUGH Task View arrives as taskUpdated instead, whose
      // handler already writes the detail cache. (#3010)
      writeTaskDetailCache(updatedEpic as unknown as Task);
    };

    const handleEpicDeleted = (raw: unknown) => {
      const { data: parsed } = decodeSocketPayload<{ id: number }>(raw);
      if (!parsed?.id) return;
      removeListItems<Epic>(queryClient, queryKeys.epics(Number(spaceId)), Number(parsed.id));
      // Same reason as the rename above: the epic's detail record is keyed by task NUMBER
      // and would otherwise be served to the next open of that URL. (#3010)
      removeTaskDetailCache([Number(parsed.id)]);
    };

    const handleViewAdded = (raw: unknown) => {
      const { data: addedView } = decodeSocketPayload<WorkspaceView>(raw);
      if (!addedView) {
        return;
      }

      updateWorkspaceViews(queryClient, queryKeys.workspace(Number(spaceId)), (views) => [
        ...views.filter((view) => view.id !== addedView.id),
        addedView,
      ]);
    };

    const handleViewUpdated = (raw: unknown) => {
      const { data: updatedView } = decodeSocketPayload<WorkspaceView>(raw);
      if (!updatedView) {
        return;
      }
      updateWorkspaceViews(queryClient, queryKeys.workspace(Number(spaceId)), (views) =>
        views.map((view) => (view.id === updatedView.id ? updatedView : view))
      );
    };

    const handleViewDefaultUpdated = (raw: unknown) => {
      const { data: updatedView } = decodeSocketPayload<WorkspaceView>(raw);
      if (!updatedView) {
        return;
      }
      updateWorkspaceViews(queryClient, queryKeys.workspace(Number(spaceId)), (views) =>
        views.map((view) =>
          view.group === updatedView.group ? { ...view, default: view.id === updatedView.id } : view
        )
      );
    };

    const handleViewDeleted = (raw: unknown) => {
      const { data: deletedView } = decodeSocketPayload<WorkspaceView>(raw);
      if (!deletedView) {
        return;
      }

      updateWorkspaceViews(queryClient, queryKeys.workspace(Number(spaceId)), (views) =>
        views.filter((view) => view.id !== deletedView.id)
      );
    };

    const handleViewsOrderUpdated = (raw: unknown) => {
      const { data: updatedViews } = decodeSocketPayload<WorkspaceView[]>(raw);
      if (!updatedViews) {
        return;
      }
      updateWorkspaceViews(queryClient, queryKeys.workspace(Number(spaceId)), (views) =>
        views.map((view) => updatedViews.find((v) => v.id === view.id) ?? view)
      );
    };

    const handleSpaceUpdated = (raw: unknown) => {
      const { data: updatedSpace } = decodeSocketPayload<Partial<Workspace>>(raw);
      if (!updatedSpace || typeof updatedSpace !== 'object') {
        return;
      }
      queryClient.setQueryData(queryKeys.workspace(Number(spaceId)), (oldData: Workspace) => ({
        ...oldData,
        ...updatedSpace,
      }));
    };

    const handleSprintUpdated = () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.sprintRoot() });
    };

    const handleImportProgress = (raw: unknown) => {
      const { data } = decodeSocketPayload<{
        importId: number;
        progress: number;
        tasksCreated: number;
        totalTasks: number;
      }>(raw);
      if (!data) return;
      queryClient.setQueryData(queryKeys.import(data.importId), (old: any) => {
        if (!old) return old;
        return { ...old, tasksCreatedCount: data.tasksCreated };
      });
    };

    const handleImportCompleted = (raw: unknown) => {
      const { data } = decodeSocketPayload<{
        importId: number;
        tasksCreatedCount: number;
        labelsCreatedCount: number;
      }>(raw);
      if (!data) return;
      queryClient.setQueryData(queryKeys.import(data.importId), (old: any) => {
        if (!old) return old;
        return {
          ...old,
          status: 'done',
          tasksCreatedCount: data.tasksCreatedCount,
          labelsCreatedCount: data.labelsCreatedCount,
        };
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks(Number(spaceId)) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.epics(Number(spaceId)) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.imports() });
      notification.open({
        key: 'import-progress',
        message: 'Import Complete!',
        description: `${data.tasksCreatedCount} tasks created.`,
        icon: <CheckCircleFilled style={{ color: '#16a34a' }} />,
        placement: 'topRight',
        duration: 5,
        style: { backgroundColor: '#e6ffed', border: '1px solid #7ff5aa', borderRadius: 8 },
      });
    };

    const handleImportFailed = (raw: unknown) => {
      const { data } = decodeSocketPayload<{ importId: number; error: string }>(raw);
      if (!data) return;
      queryClient.setQueryData(queryKeys.import(data.importId), (old: any) => {
        if (!old) return old;
        return { ...old, status: 'failed', error: data.error };
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.imports() });
      notification.open({
        key: 'import-progress',
        message: 'Import Failed',
        description: data.error,
        icon: <WarningFilled style={{ color: '#dc2626' }} />,
        placement: 'topRight',
        duration: 8,
        style: { backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 },
      });
    };

    // All three planning events target a single query key — share the handler.
    const handlePlanning = () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.planningSession(parseInt(spaceId)),
        refetchType: 'active',
      });
    };

    // Backfill space-scoped queries that could be stale after a navigation absence
    // or a dropped socket. `refetchType: 'active'` keeps the fetch narrow to mounted
    // views. In-flight mutations defer the backfill rather than cancel it — dropping it
    // outright meant a reconnect landing during any mutation lost every event missed
    // while disconnected, permanently (audit F6). Kill switch: VITE_REALTIME_BACKFILL=false.
    const backfill = () => {
      if (import.meta.env.VITE_REALTIME_BACKFILL === 'false') return;
      runWhenSettled(
        (cb) => whenAllMutationsSettle(queryClient, cb),
        () => {
          const spaceKey = Number(spaceId);
          void Promise.allSettled([
            queryClient.invalidateQueries({ queryKey: queryKeys.tasks(spaceKey), refetchType: 'active' }),
            queryClient.invalidateQueries({ queryKey: queryKeys.epics(spaceKey), refetchType: 'active' }),
            queryClient.invalidateQueries({ queryKey: queryKeys.workspace(spaceKey), refetchType: 'active' }),
          ]);
        }
      );
    };
    const handleReconnect = () => {
      // Re-join the space room on every (re)connect. Socket.IO drops a socket's
      // room membership on disconnect, so after a reconnect (e.g. a deploy
      // recreates the server) the socket is connected but in no rooms — live
      // sync/reordering silently dies until a full page reload. Re-emitting for
      // the same id is a server-side no-op.
      socket.emit('backlog', workspaceQuery.data?.id);
      backfill();
    };

    // Single source of truth for register + cleanup. 'connect' is allowed so the
    // reconnect backfill handler plugs in without widening the list.
    type SocketEventName = SocketEvent | 'connect';
    const handlers: Array<[SocketEventName, (payload: any) => void]> = [
      ['connect', handleReconnect],
      [SocketEvent.taskUpdated, handleTaskUpdated],
      [SocketEvent.taskAdded, handleTaskAdded],
      [SocketEvent.taskDeleted, handleTaskDeleted],
      [SocketEvent.taskCommentsChanged, handleTaskCommentsChanged],
      [SocketEvent.taskActivityChanged, handleTaskActivityChanged],
      [SocketEvent.taskDependenciesChanged, handleTaskDependenciesChanged],
      [SocketEvent.tasksUpdated, handleTasksUpdated],
      [SocketEvent.tasksDeleted, handleTasksDeleted],
      [SocketEvent.epicUpdated, handleEpicUpdated],
      [SocketEvent.epicDeleted, handleEpicDeleted],
      [SocketEvent.viewAdded, handleViewAdded],
      [SocketEvent.viewUpdated, handleViewUpdated],
      [SocketEvent.viewDefaultUpdated, handleViewDefaultUpdated],
      [SocketEvent.viewDeleted, handleViewDeleted],
      [SocketEvent.viewsOrderUpdated, handleViewsOrderUpdated],
      [SocketEvent.spaceUpdated, handleSpaceUpdated],
      [SocketEvent.sprintUpdated, handleSprintUpdated],
      [SocketEvent.planning, handlePlanning],
      [SocketEvent.planningChanged, handlePlanning],
      [SocketEvent.planningAgendaChanged, handlePlanning],
      [SocketEvent.importProgress, handleImportProgress],
      [SocketEvent.importCompleted, handleImportCompleted],
      [SocketEvent.importFailed, handleImportFailed],
    ];

    // --- Connect and register ---

    void connectToSocket().then(() => {
      if (aborted) return;
      socket.emit('backlog', workspaceQuery.data?.id);
      handlers.forEach(([event, handler]) => socket.on(event, handler));
      // Initial mount backfill: covers events missed while the user was off this space.
      backfill();
    });

    return () => {
      aborted = true;
      handlers.forEach(([event, handler]) => socket.off(event, handler));
      pendingReconciles.forEach((cancel) => cancel());
      pendingReconciles.clear();
    };
  }, [workspaceQuery.data?.id, spaceId]);
};
